import React, { FC, useEffect, useState, useCallback } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Asset, Entry } from "contentful";
import { Document } from "@contentful/rich-text-types";
import CTA, { CTAProps } from "../CTA";
import {
  Figure,
  BackgroundContainer,
  Video,
  Image,
  ButtonContainer,
  Caption,
  LogoImage,
  ContentWrapper,
} from "./styles";

export interface HeroModuleProps extends Entry {
  id: string,
  brand: string,
  fields: {
    mainMedia?: Asset;
    mobileMedia?: Asset;
    altText?: string;
    logoImage?: Asset;
    logoMaxWidth?: number;
    textContent?: Document;
    contentAlignment?: string;
    mobileContentAlignment?: string;
    contentMaxWidth?: number;
    theme?: string;
    useBackgroundOverlay?: boolean;
    ctas?: CTAProps[];
    showBehindNavigation?: boolean;
  };
}

const RenderedAsset: FC<{ asset: Asset; altText?: string }> = ({
  asset,
  altText,
}) => {
  const contentType = asset.fields.file?.contentType as string;

  if (contentType.includes("video")) {
    return (
      <Video autoPlay loop muted playsInline>
        <source src={`https:${asset.fields.file?.url}`} type={contentType} />
      </Video>
    );
  }

  return <Image src={`https:${asset.fields.file?.url}`} alt={altText} />;
};

interface ContentMarkupProps {
  brand: string;
  fields: HeroModuleProps['fields'];
  isMobile: boolean;
}

const ContentMarkup: FC<ContentMarkupProps> = ({ brand, fields, isMobile }) => {
  const {
    contentAlignment,
    contentMaxWidth,
    mobileContentAlignment,
    logoImage,
    logoMaxWidth,
    textContent,
    theme,
    ctas,
  } = fields;

  return (
    <ContentWrapper
      $contentAlignment={contentAlignment}
      $isMobile={isMobile}
      $maxWidth={contentMaxWidth}
      $mobileContentAlignment={mobileContentAlignment}
      $theme={theme}
      $brand={brand}
    >
      {logoImage && logoImage.fields.file && (
        <LogoImage
          alt="Logo"
          src={`https:${logoImage.fields.file.url}`}
          $maxWidth={logoMaxWidth}
        />
      )}
      {textContent && (
        <Caption
          $contentAlignment={contentAlignment}
          $isMobile={isMobile}
          $maxWidth={contentMaxWidth}
          $theme={theme}
          $brand={brand}
          $mobileContentAlignment={mobileContentAlignment}
        >
          {documentToReactComponents(textContent)}
          {ctas && (
            <ButtonContainer $contentAlignment={contentAlignment}>
              {ctas.map((cta, index) => (
                <CTA key={index} {...cta} brand={brand} />
              ))}
            </ButtonContainer>
          )}
        </Caption>
      )}
    </ContentWrapper>
  );
}

const HeroModule: FC<HeroModuleProps> = ({ id, brand, fields }) => {
  const {
    mainMedia,
    mobileMedia,
    altText,
    contentAlignment,
    mobileContentAlignment,
    theme,
    useBackgroundOverlay,
    showBehindNavigation = false,
  } = fields;
  const [isMobile, setIsMobile] = useState(false);

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const mediaToShow = isMobile ? mobileMedia || mainMedia : mainMedia;

  let extraHeight = 0;
  if (showBehindNavigation) {
    const headerWrapperDiv = document.getElementsByClassName('header-wrapper');
    const utilityBar = document.getElementsByClassName('utility-bar');

    if (headerWrapperDiv) {
      // @ts-ignore
      headerWrapperDiv[0].style.background = 'transparent';
      // @ts-ignore
      extraHeight += headerWrapperDiv[0].offsetHeight;
    }

    if (utilityBar) {
      // @ts-ignore
      utilityBar[0].style.background = 'transparent';
      // @ts-ignore
      extraHeight += utilityBar[0].offsetHeight;
    }
  }

  return (
    <>
      <Figure
        data-component-id={id}
        $contentAlignment={contentAlignment || 'center'}
        $isMobile={isMobile}
        $mobileContentAlignment={mobileContentAlignment || 'center'}
        $theme={theme}
        $useBackgroundOverlay={useBackgroundOverlay || false}
        $brand={brand}
      >
        {mediaToShow && (
          <BackgroundContainer
            $transparentHeaderHeight={extraHeight}
            $useTransparentHeader={showBehindNavigation}
          >
            <RenderedAsset asset={mediaToShow} altText={altText} />
          </BackgroundContainer>
        )}
        {(mobileContentAlignment != 'below' || !isMobile) && (
          <ContentMarkup brand={brand} fields={fields} isMobile={isMobile} />
        )}
      </Figure>
      {(mobileContentAlignment == 'below' && isMobile) && (
        <ContentMarkup brand={brand} fields={fields} isMobile={isMobile} />
      )}
    </>
  );
};

export default HeroModule;
